/**
 * Dynamic favicon dot — overlays a small red dot on the existing favicon
 * when there are unread WhatsApp messages, mimicking WhatsApp Web / Slack.
 *
 * We render to an off-screen canvas, then swap the document's favicon
 * `<link rel="icon">` href to the resulting data URL. Cached on first
 * load so we don't redraw the base image every poll.
 */
const FAVICON_URL = '/favicon.png';
const SIZE = 32;

let _baseImage = null;
let _baseLoaded = false;
let _cleanFaviconDataUrl = null;  // base, no dot — used to "clear"
let _currentlyDirty = false;

const loadBase = () =>
  new Promise((resolve) => {
    if (_baseLoaded) return resolve(_baseImage);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      _baseImage = img;
      _baseLoaded = true;
      resolve(img);
    };
    img.onerror = () => {
      _baseLoaded = true;
      resolve(null);
    };
    img.src = FAVICON_URL;
  });

const getLink = () => {
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  return link;
};

const drawCleanIfNeeded = (img) => {
  if (_cleanFaviconDataUrl) return _cleanFaviconDataUrl;
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');
  if (img) ctx.drawImage(img, 0, 0, SIZE, SIZE);
  _cleanFaviconDataUrl = canvas.toDataURL('image/png');
  return _cleanFaviconDataUrl;
};

export const setFaviconDot = async (hasUnread) => {
  try {
    const img = await loadBase();
    drawCleanIfNeeded(img);

    if (!hasUnread) {
      if (!_currentlyDirty) return;
      getLink().href = _cleanFaviconDataUrl;
      _currentlyDirty = false;
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = SIZE;
    canvas.height = SIZE;
    const ctx = canvas.getContext('2d');
    if (img) ctx.drawImage(img, 0, 0, SIZE, SIZE);

    // Red dot — top-right corner with a subtle white ring for contrast on
    // dark and light favicons.
    const r = 8;
    const cx = SIZE - r - 1;
    const cy = r + 1;
    ctx.beginPath();
    ctx.arc(cx, cy, r + 1.5, 0, Math.PI * 2);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = '#ef4444';  // tailwind red-500
    ctx.fill();

    getLink().href = canvas.toDataURL('image/png');
    _currentlyDirty = true;
  } catch {
    // Favicon updates are best-effort.
  }
};
